/**
 * Wishlist Management System
 * Uses localStorage for persistent wishlist storage
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.wishlist = this.loadWishlist();
    this.init();
  }

  init() {
    // Update UI on page load
    this.updateAllUI();
    
    // Listen for storage changes (sync across tabs)
    window.addEventListener('storage', (e) => {
      if (e.key === this.storageKey) {
        this.wishlist = this.loadWishlist();
        this.updateAllUI();
      }
    });
  }

  loadWishlist() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Error loading wishlist:', e);
      return [];
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      this.updateAllUI();
      
      // Trigger custom event for other components
      window.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: { count: this.wishlist.length, items: this.wishlist }
      }));
    } catch (e) {
      console.error('Error saving wishlist:', e);
    }
  }

  addToWishlist(productId, productData = {}) {
    // Check if already in wishlist
    const exists = this.wishlist.find(item => item.id === productId);
    if (exists) {
      return false;
    }

    const wishlistItem = {
      id: productId,
      title: productData.title || '',
      price: productData.price || '',
      image: productData.image || '',
      url: productData.url || '',
      addedAt: new Date().toISOString()
    };

    this.wishlist.push(wishlistItem);
    this.saveWishlist();
    
    // Show notification
    this.showNotification('Added to wishlist');
    
    return true;
  }

  removeFromWishlist(productId) {
    const initialLength = this.wishlist.length;
    this.wishlist = this.wishlist.filter(item => item.id !== productId);
    
    if (this.wishlist.length < initialLength) {
      this.saveWishlist();
      this.showNotification('Removed from wishlist');
      return true;
    }
    
    return false;
  }

  toggleWishlist(productId, productData = {}) {
    if (this.isInWishlist(productId)) {
      this.removeFromWishlist(productId);
      return false;
    } else {
      this.addToWishlist(productId, productData);
      return true;
    }
  }

  isInWishlist(productId) {
    return this.wishlist.some(item => item.id === productId);
  }

  getWishlist() {
    return [...this.wishlist];
  }

  getWishlistCount() {
    return this.wishlist.length;
  }

  clearWishlist() {
    this.wishlist = [];
    this.saveWishlist();
    this.showNotification('Wishlist cleared');
  }

  updateAllUI() {
    this.updateWishlistButtons();
    this.updateWishlistCount();
  }

  updateWishlistButtons() {
    // Update all wishlist buttons on the page
    document.querySelectorAll('[data-wishlist-button]').forEach(button => {
      const productId = button.dataset.productId;
      if (productId) {
        const isInWishlist = this.isInWishlist(productId);
        button.classList.toggle('active', isInWishlist);
        button.setAttribute('aria-pressed', isInWishlist);
        
        const icon = button.querySelector('.wishlist-icon');
        if (icon) {
          icon.innerHTML = isInWishlist ? this.getHeartIconFilled() : this.getHeartIconOutline();
        }
      }
    });
  }

  updateWishlistCount() {
    const count = this.getWishlistCount();
    
    // Update count badges
    document.querySelectorAll('[data-wishlist-count]').forEach(element => {
      element.textContent = count;
    });

    // Update wishlist count wrappers (show/hide parentheses)
    document.querySelectorAll('.wishlist-count-wrapper').forEach(wrapper => {
      wrapper.style.display = count > 0 ? 'inline' : 'none';
    });

    // Update standalone count badges
    document.querySelectorAll('.header-wishlist-count').forEach(badge => {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  showNotification(message) {
    // Remove existing notifications
    const existing = document.querySelector('.wishlist-notification');
    if (existing) {
      existing.remove();
    }

    // Create notification
    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.textContent = message;
    document.body.appendChild(notification);

    // Trigger animation
    setTimeout(() => notification.classList.add('show'), 10);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  getHeartIconOutline() {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg>`;
  }

  getHeartIconFilled() {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg>`;
  }

  // Export wishlist for analytics or external use
  exportWishlist() {
    return {
      count: this.getWishlistCount(),
      items: this.getWishlist(),
      exportedAt: new Date().toISOString()
    };
  }
}

// Initialize wishlist manager
const wishlistManager = new WishlistManager();

// Make it globally accessible
window.wishlistManager = wishlistManager;

// Helper function for quick access
window.toggleWishlist = function(productId, productData) {
  return wishlistManager.toggleWishlist(productId, productData);
};

// Event delegation for wishlist buttons
document.addEventListener('click', function(e) {
  const button = e.target.closest('[data-wishlist-button]');
  if (button) {
    e.preventDefault();
    const productId = button.dataset.productId;
    const productData = {
      title: button.dataset.productTitle,
      price: button.dataset.productPrice,
      image: button.dataset.productImage,
      url: button.dataset.productUrl
    };
    
    wishlistManager.toggleWishlist(productId, productData);
  }
});

console.log('Wishlist Manager initialized');
